/**
 * Version Check Utility
 * Detects when a new version of the app is deployed and prompts users to refresh
 */

let currentVersion = null;
let checkInterval = null;

/**
 * Initialize version checking
 * @param {Function} onNewVersion - Callback when new version detected
 */
export const initVersionCheck = (onNewVersion) => {
  // Fetch initial version
  fetchVersion().then(version => {
    currentVersion = version;
    if (process.env.NODE_ENV === 'development') console.log('[Version Check] Initial version:', version);
  }).catch(err => {
    console.warn('[Version Check] Failed to fetch initial version:', err);
  });

  // Check for updates every 5 minutes
  checkInterval = setInterval(() => {
    checkForUpdates(onNewVersion);
  }, 5 * 60 * 1000); // 5 minutes
};

/**
 * Stop version checking (cleanup)
 */
export const stopVersionCheck = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
};

/**
 * Fetch current version from server
 */
const fetchVersion = async () => {
  try {
    // Add timestamp to prevent caching
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return data.version || data.buildTime;
  } catch (error) {
    // Fallback: use meta tag if version.json doesn't exist
    const metaVersion = document.querySelector('meta[name="app-version"]');
    if (metaVersion) {
      return metaVersion.content;
    }
    throw error;
  }
};

/**
 * Check if a new version is available
 */
const checkForUpdates = async (onNewVersion) => {
  try {
    const latestVersion = await fetchVersion();
    
    if (currentVersion && latestVersion !== currentVersion) {
      console.log('[Version Check] New version detected:', latestVersion, '(current:', currentVersion + ')');
      if (onNewVersion) {
        onNewVersion(latestVersion);
      }
      // Update current version to prevent repeated notifications
      currentVersion = latestVersion;
    }
  } catch (error) {
    console.warn('[Version Check] Update check failed:', error.message);
  }
};

/**
 * Force reload the page (clears cache)
 */
export const reloadApp = () => {
  window.location.reload(true);
};
